import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineTag, HiChevronDown } from "react-icons/hi";
import NoTranslate from "../../../atoms/NoTranslate";
import { TodoBadge } from "../../../../types/TodoTypes";
import { badgeSwatchStyle } from "./todoBadges";

interface Props {
  badges: TodoBadge[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

const BadgeFilter: React.FC<Props> = ({ badges, selectedIds, onToggle }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (badges.length === 0) return null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
          open || selectedIds.length > 0
            ? "bg-blue-50 border-blue-300 text-blue-600"
            : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
        }`}
      >
        <HiOutlineTag size={16} />
        {t("todos.filterBadge.label")}
        {selectedIds.length > 0 && (
          <span className="ml-0.5 text-xs">({selectedIds.length})</span>
        )}
        <HiChevronDown
          size={14}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 min-w-[180px] max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg p-1">
          {badges.map((b) => {
            const active = selectedIds.includes(b.id);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => onToggle(b.id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-gray-50 ${
                  active ? "bg-blue-50/60" : ""
                }`}
              >
                <span
                  style={badgeSwatchStyle(b.color)}
                  className="w-2.5 h-2.5 rounded-full"
                />
                <span className="flex-1 text-sm text-gray-700 truncate">
                  <NoTranslate>{b.label}</NoTranslate>
                </span>
                {active && <span className="text-blue-600 text-xs">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BadgeFilter;
